import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Send, Refresh } from '@mui/icons-material';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';

import { validateInput, rateLimiter } from '../utils/validation';
import { saveConversationExchange } from '../firestoreService';
import { API_CONFIG, LIMITS } from '../config';
import { glassStyles, colors } from '../theme';
import Loading from '../components/Loading';

const Chat = () => {
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]); // Full chat history
  const [audioUrl, setAudioUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, [auth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userId = user?.uid || 'anonymous';
    if (!rateLimiter.isAllowed(userId, LIMITS.rateLimitRequests, LIMITS.rateLimitWindowMs)) {
      setError('Too many requests. Please wait a moment.');
      return;
    }

    const validation = validateInput.validateMessage(message);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    const userMessage = validation.sanitized;

    // Add user message to history immediately
    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: userMessage },
    ];
    setConversationHistory(updatedHistory);

    setLoading(true);
    setError('');
    setAudioUrl('');
    setMessage('');

    try {
      console.log('Sending to backend:', {
        text: userMessage,
        username: user?.displayName || 'Anonymous',
        conversationHistory: updatedHistory,
      });

      const interpretRes = await axios.post(`${API_CONFIG.baseUrl}/interpret`, {
        text: userMessage,
        username: user?.displayName || 'Anonymous',
        conversationHistory: updatedHistory, // Send full history to API
      });

      const aiResponse = interpretRes.data.summary;

      // Add AI response to history
      setConversationHistory((prev) => [
        ...prev,
        { role: 'assistant', content: aiResponse },
      ]);

      saveConversationExchange(
        userMessage,
        aiResponse,
        user?.displayName || 'Anonymous',
        user?.email || null
      ).catch(console.error);

      const audioRes = await axios.post(
        `${API_CONFIG.baseUrl}/generate-audio`,
        {
          text: aiResponse,
          voice_settings: { similarity_boost: 0.5, stability: 0.3, style: 0.25 },
        },
        { responseType: 'blob' }
      );

      setAudioUrl(URL.createObjectURL(audioRes.data));
    } catch (err) {
      console.error('Error:', err);
      setError('Something went wrong. Please try again.');
      // Remove the user message if request failed
      setConversationHistory(conversationHistory);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessage('');
    setConversationHistory([]);
    setAudioUrl('');
    setError('');
  };

  // Full screen loading state
  if (loading) {
    return <Loading message="Nick is thinking..." />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 0, sm: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ ...glassStyles.cardElevated, p: { xs: 2.5, sm: 4 } }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                }}
              >
                Ask Nick
              </Typography>
              <Typography variant="body1" color="text.secondary">
                AI-powered responses in Nick's voice
              </Typography>
            </Box>

            {/* Input form */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="What do you want to ask Nick?"
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= LIMITS.maxMessageLength) {
                    setMessage(e.target.value);
                    setError('');
                  }
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.muted">
                  {message.length}/{LIMITS.maxMessageLength}
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!message.trim()}
                  endIcon={<Send />}
                >
                  Send
                </Button>
              </Box>
            </form>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Conversation History */}
            {conversationHistory.length > 0 && (
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {conversationHistory.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        ...glassStyles.subtle,
                        p: 2,
                        borderLeft: msg.role === 'user'
                          ? `3px solid ${colors.secondary.main}`
                          : `3px solid ${colors.primary.main}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: msg.role === 'user' ? colors.secondary.light : colors.primary.light,
                          mb: 0.5,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      >
                        {msg.role === 'user' ? 'You' : 'Nick'}
                      </Typography>
                      <Typography variant="body2">{msg.content}</Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            )}

            {/* Audio player */}
            <AnimatePresence>
              {audioUrl && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <Box sx={{ ...glassStyles.subtle, p: 2, mb: 2 }}>
                    <audio
                      src={audioUrl}
                      controls
                      autoPlay
                      style={{ width: '100%', height: 40 }}
                    />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Clear button */}
            {conversationHistory.length > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={clearChat}
                  startIcon={<Refresh />}
                  sx={{ color: 'text.secondary', borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  New conversation
                </Button>
              </Box>
            )}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Chat;
